/**
 * Which environment a build is, and what production is already running.
 *
 * Every app built from this template has two long-lived environments
 * (ADR sandbox-before-live, docs/ENVIRONMENTS.md):
 *
 *  - **production** — what real people use. Built from `main`.
 *  - **sandbox**    — where every change is reviewed by hand first. Built from
 *                     `sandbox`, with its own database.
 *
 * Anything else (a laptop, a test run, a pull-request preview) is
 * **development**.
 *
 * Production publishes a tiny manifest at `GET /api/build` — which environment
 * it is, which commit, and the slugs of the change-log entries it contains.
 * Sandbox reads it to work out what is waiting for approval. The manifest is
 * public on purpose, so it must never carry more than this: see
 * `buildManifestSchema`, which is strict.
 */
import { z } from 'zod'
import type { ChangeEntry } from './change-log.ts'

export type AppEnvironment = 'production' | 'sandbox' | 'development'

/**
 * Read `VITE_ENVIRONMENT`. Only the two exact words count — anything else,
 * including unset, is development, so a typo can never make a laptop claim to
 * be production (which would switch session replay on).
 */
export function toEnvironment(value: string | undefined | null): AppEnvironment {
  return value === 'production' || value === 'sandbox' ? value : 'development'
}

/** Slugs are file names, which are harmless: `0057-sandbox-before-live`. */
const slug = z.string().regex(/^\d+-[a-z0-9-]+$/)

/**
 * `GET /api/build`. Strict: a field added here is a field published to the
 * whole internet, and `build-manifest.test.ts` fails until it is thought about.
 */
export const buildManifestSchema = z
  .object({
    environment: z.enum(['production', 'sandbox', 'development']),
    /** The git commit this server was built from; null when it is not known. */
    commit: z
      .string()
      .regex(/^[0-9a-f]{7,40}$/)
      .nullable(),
    /** Every change-log entry this build contains, newest first. */
    changes: z.array(slug),
  })
  .strict()

export type BuildManifest = z.infer<typeof buildManifestSchema>

/** One entry waiting for approval, as the Sandbox banner lists it. */
export const waitingChangeSchema = z.object({
  slug,
  title: z.string(),
  summary: z.string(),
  date: z.string(),
})

export type WaitingChange = z.infer<typeof waitingChangeSchema>

/**
 * `GET /api/build/waiting` — sandbox only, signed in. `unreachable` means
 * production did not answer (or answered with something that is not a
 * production manifest), so the banner says so rather than guessing.
 */
export const waitingResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ok'),
    productionCommit: z.string().nullable(),
    changes: z.array(waitingChangeSchema),
  }),
  z.object({ status: z.literal('unreachable') }),
])

export type WaitingResponse = z.infer<typeof waitingResponseSchema>

/**
 * The entries in this build that production does not have yet, newest first.
 * Compared by slug, never by git history — a Railway build has no `.git`.
 */
export function waitingChanges(own: ChangeEntry[], productionSlugs: string[]): WaitingChange[] {
  const live = new Set(productionSlugs)
  return [...own]
    .sort((a, b) => b.order - a.order)
    .filter((entry) => !live.has(entry.slug))
    .map(({ slug, title, summary, date }) => ({ slug, title, summary, date }))
}

/** Every email subject from a sandbox says so, in one place (src/adapters/email). */
export function subjectFor(subject: string, environment: AppEnvironment): string {
  return environment === 'sandbox' ? `[Sandbox] ${subject}` : subject
}
